import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Author, PostSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { getAuthor, getPosts } from '@/lib/journal'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ContactCta } from '@/components/generals/contact-cta'
import RichText from '@/components/generals/rich-text'
import { buttonVariants } from '@/components/ui/button'
import { PostCard } from '@/components/journal/post-card'
import { siteImages } from '@/constants/site'

interface AboutPageProps {
    author: Author | null
    recent: PostSummary[]
}

const steps = [
    {
        title: "Listen",
        text: "We start with how you live: your routines, the pieces you love and what isn't working today."
    },
    {
        title: "Plan",
        text: "Layouts, lighting and a material board, so every decision fits the whole before anything is ordered."
    },
    {
        title: "Source",
        text: "Furniture, finishes and the occasional vintage find, chosen to last and to suit your budget."
    },
    {
        title: "Style",
        text: "The final layer of art, textiles and objects that makes the house feel unmistakably yours."
    }
]

export const getServerSideProps: GetServerSideProps<AboutPageProps> = async () => {
    try {
        const [author, recent] = await Promise.all([getAuthor(), getPosts({ limit: 3 })])

        return { props: { author, recent } }
    }
    catch (error) {
        console.error('The about page could not load the author:', error instanceof Error ? error.message : error)

        return { props: { author: null, recent: [] } }
    }
}

const AboutPage: FC<AboutPageProps> = ({ author, recent }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="About"
                description={author?.bio ?? "Meet the interior designer behind the journal, and how a warm, lived-in home comes together."}
                ogImage={author?.avatar ?? undefined}
            />

            <section id='about' className='mx-auto grid max-w-7xl items-center gap-16 px-5 pt-12 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pt-20 lg:pb-28'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out motion-reduce:animate-none lg:col-span-7'>
                    {author?.role ? (
                        <Eyebrow>
                            <span data-mdlfy-cms={`authors:${author.id}:role`}>{author.role}</span>
                        </Eyebrow>
                    ) : (
                        <Eyebrow>
                            About
                        </Eyebrow>
                    )}

                    <h1 className='mt-7 font-heading text-5xl leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl'>
                        {author ? (
                            <>
                                Hello, I'm <em data-mdlfy-cms={`authors:${author.id}:name`} className='text-primary'>{author.name}</em>
                            </>
                        ) : (
                            "Hello, and welcome"
                        )}
                    </h1>

                    {author?.bio && (
                        <p
                            data-mdlfy-cms={`authors:${author.id}:bio`}
                            className='mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl'
                        >
                            {author.bio}
                        </p>
                    )}

                    <div className='mt-10 flex flex-wrap items-center gap-3'>
                        <Link href='/contact' className={buttonVariants({ size: 'lg', className: 'group' })}>
                            Work with me
                            <ArrowRight className='transition-transform group-hover:translate-x-0.5' />
                        </Link>

                        <Link href='/journal' className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                            Read the journal
                        </Link>
                    </div>
                </div>

                <div className='lg:col-span-5'>
                    <div className='relative mx-auto max-w-md'>
                        <div
                            aria-hidden='true'
                            className='absolute -top-5 -right-5 bottom-16 left-10 rounded-t-full border border-primary/30'
                        />

                        <img
                            src={author?.avatar ?? siteImages.portrait.src}
                            alt={author?.avatar ? (author.avatar_alt ?? '') : siteImages.portrait.alt}
                            fetchPriority='high'
                            decoding='async'
                            className='relative aspect-[4/5] w-full rounded-t-full rounded-b-3xl bg-muted object-cover'
                        />
                    </div>
                </div>
            </section>

            {author?.story && (
                <section id='story' className='border-t border-border'>
                    <div className='mx-auto grid max-w-7xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28'>
                        <div className='lg:sticky lg:top-28 lg:col-span-4 lg:self-start'>
                            <Eyebrow>My story</Eyebrow>

                            <h2 className='mt-5 font-heading text-4xl tracking-tight sm:text-5xl'>
                                Why I do this work
                            </h2>
                        </div>

                        <div className='lg:col-span-7 lg:col-start-6'>
                            <RichText html={author.story} className='text-lg leading-[1.85] text-foreground/90 sm:text-xl sm:leading-[1.8]' />
                        </div>
                    </div>
                </section>
            )}

            <section id='process' className='bg-secondary/60'>
                <div className='mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28'>
                    <div className='max-w-2xl'>
                        <Eyebrow>How I work</Eyebrow>

                        <h2 className='mt-5 font-heading text-4xl tracking-tight sm:text-5xl'>
                            From first conversation to the final cushion
                        </h2>
                    </div>

                    <ol className='mt-14 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4'>
                        {steps.map((step, index) => (
                            <li key={step.title} className='border-t border-foreground/15 pt-6'>
                                <span className='font-heading text-sm text-primary tabular-nums'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h3 className='mt-3 font-heading text-2xl'>
                                    {step.title}
                                </h3>

                                <p className='mt-3 leading-relaxed text-muted-foreground'>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {recent.length > 0 && (
                <section id='recent-stories' className='mx-auto max-w-7xl px-5 pt-20 sm:px-8 lg:pt-28'>
                    <div className='flex flex-col gap-6 border-b border-border pb-8 sm:flex-row sm:items-end sm:justify-between'>
                        <div>
                            <Eyebrow>From the journal</Eyebrow>

                            <h2 className='mt-5 font-heading text-4xl tracking-tight sm:text-5xl'>
                                Recent writing
                            </h2>
                        </div>

                        <Link href='/journal' className='group inline-flex items-center gap-2 self-start font-medium sm:self-auto'>
                            All stories
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </Link>
                    </div>

                    <div className='mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3'>
                        {recent.map(post => (
                            <PostCard key={post.id} post={post} />
                        ))}
                    </div>
                </section>
            )}

            <ContactCta />
        </Layout>
    )
}

export default AboutPage
